import type { AnyRouter } from "@tanstack/react-router";

import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import React from "react";

export type AuthContextType = {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any;
  loginWithRedirect: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessTokenSilently: () => Promise<string>;
};

const AuthContext = React.createContext<AuthContextType | null>(null);

function InternalAuthProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user, getAccessTokenSilently, loginWithRedirect, logout } =
    useAuth0();

  const auth0Logout = async () => {
    logout({
      clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
      logoutParams: { returnTo: import.meta.env.VITE_APP_BASE_URL },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        loginWithRedirect,
        logout: auth0Logout,
        getAccessTokenSilently,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

interface AuthProvider {
  children: React.ReactElement;
  router: AnyRouter;
}

export function AuthProvider({ children, router }: AuthProvider) {
  return (
    <Auth0Provider
      authorizationParams={{
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        redirect_uri: window.location.origin,
        scope: "openid profile email phone offline_access",
      }}
      cacheLocation="localstorage"
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      onRedirectCallback={(appState) => {
        const to = appState?.returnTo === "/sign-in" ? "/" : appState?.returnTo || "/";
        router.navigate({ replace: true, to });
      }}
    >
      <InternalAuthProvider>{children}</InternalAuthProvider>
    </Auth0Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
